import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { personKeys } from "@/lib/query";

const currentPersonSchema = z.object({
  personId: z.string(),
  email: z.string(),
  name: z.string(),
  managerPersonId: z.string().nullable(),
  isFullTime: z.boolean(),
  managesAnyone: z.boolean(),
  /** The server switch EXPENSES_ENABLED. */
  expensesEnabled: z.boolean().default(false),
});

export type CurrentPersonView = z.infer<typeof currentPersonSchema>;

/** The server could not match the signed-in user to exactly one active Person (403). */
export class PersonAccessError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PersonAccessError";
  }
}

/** `/api/me` answered 401: the session is gone, so the guard sends the user to sign in. */
export class SessionExpiredError extends Error {
  constructor() {
    super("Your session has expired.");
    this.name = "SessionExpiredError";
  }
}

const refusalSchema = z.object({ error: z.string() });

async function fetchCurrentPerson(): Promise<CurrentPersonView> {
  const res = await fetch("/api/me", { headers: { accept: "application/json" } });
  if (res.status === 403) {
    const body: unknown = await res.json().catch(() => null);
    throw new PersonAccessError(
      refusalSchema.safeParse(body).data?.error ?? "Your sign-in isn't linked to a Nymbl person.",
    );
  }
  if (res.status === 401) throw new SessionExpiredError();
  if (!res.ok) throw new Error(`Couldn't load your profile (${res.status})`);
  return currentPersonSchema.parse(await res.json());
}

export const currentPersonQueryOptions = queryOptions({
  queryKey: personKeys.me,
  queryFn: fetchCurrentPerson,
  staleTime: 5 * 60_000,
  // A 403 won't fix itself on retry; an outage might.
  retry: (count, err) =>
    !(err instanceof PersonAccessError || err instanceof SessionExpiredError) && count < 1,
});

/** The signed-in user's warehouse Person, from `GET /api/me`. */
export function useCurrentPerson() {
  return useQuery(currentPersonQueryOptions);
}
